import { Fragment } from "react";
import { Outlet, Route } from "react-router";

import { Forbiddenpage } from "src/components/PageState/ForbiddenPage";
import { ORGANIZATION, PRODUCT, useFoodpilotContext, VIEW } from "src/context/FoodpilotContext";
import { Slugs } from "src/context/ProtectedContext";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";

import { TransportLayout } from "./TransportLayout";
import { TransportOverview } from "./TransportOverview";
import { TransportLegCreatePage } from "./TransportLegCreatePage";
import { TransportCircuitCreatePage } from "./TransportCircuitCreatePage";
import { RouteErrorPage } from "src/components/PageState/RouteErrorPage";

export const useTransportRouter = () => {
    return (
        <Fragment>
            <Route path="transport" element={<TransportFeatureGate />} errorElement={<RouteErrorPage />}>
                <Route element={<TransportActionProtection slug={VIEW} />}>
                    <Route element={<TransportPresentationLayout />}>
                        <Route index element={<TransportOverview />} />
                        <Route path="legs/new" element={<TransportLegCreatePage />} />
                        <Route path="legs/:legId/edit" element={<TransportLegCreatePage />} />
                        <Route path="circuits/new" element={<TransportCircuitCreatePage />} />
                        <Route path="circuits/:circuitId/edit" element={<TransportCircuitCreatePage />} />
                    </Route>
                </Route>
            </Route>
        </Fragment>
    );
};

/**
 * Frontend lock that complements the backend menu sync: even if a user types
 * `/company/:id/transport` directly while `FS-manage-my-transport` is OFF, this
 * component renders a Forbidden page. Together with the backend gate (the menu
 * item is set isActive=false on flag deactivation), this guarantees the
 * Level 2 module is only reachable when explicitly enabled in Settings →
 * Feature Activation.
 */
const TransportFeatureGate = () => {
    const { isFeatureActivated, isLoading } = useApplicationParameters();

    if (isLoading) {
        return null;
    }

    if (!isFeatureActivated("FS-manage-my-transport")) {
        return <Forbiddenpage />;
    }

    return <Outlet />;
};

const TransportPresentationLayout = () => {
    const { isFeatureActivated } = useApplicationParameters();

    return <TransportLayout appearance={isFeatureActivated("FS-product-ux-v2") ? "product-v2" : "legacy"} />;
};

type TransportActionProtectionProps = {
    slug: Slugs;
};
const TransportActionProtection = (props: TransportActionProtectionProps) => {
    const { slug } = props;
    const { canUser } = useFoodpilotContext();

    if (canUser(slug, PRODUCT) === false && canUser(slug, ORGANIZATION) === false) {
        return <Forbiddenpage />;
    }

    return <Outlet />;
};
